import { Field } from "./Field";
import {
  PERSONAL_FIELDS,
  EMERGENCY_FIELDS,
  TEAM_LABELS,
  SOCIAL_LABELS,
  SIZE_LABELS,
  PRO_LABELS,
  PLATFORM_LABELS,
  BANKING_FIELDS,
  BRAND_GROWTH_FIELDS,
  DOC_TYPE_LABELS,
} from "@/lib/labels";

const Item = ({ label, value }) => (
  <div>
    <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-slate-500">{label}</p>
    <p className="text-sm text-slate-200 mt-0.5 break-words">{value || "—"}</p>
  </div>
);

const Section = ({ title, children }) => (
  <div className="xy-card p-5 md:p-6">
    <p className="font-display font-semibold text-slate-100 mb-4">{title}</p>
    {children}
  </div>
);

export const StepReview = ({ form, update }) => {
  const filledTeam = Object.entries(TEAM_LABELS).filter(([k]) => form.team[k].name);
  const filledSocials = Object.entries(SOCIAL_LABELS).filter(([k]) => form.socials[k]);
  const filledSizes = Object.entries(SIZE_LABELS).filter(([k]) => form.sizes[k]);
  const filledPros = Object.entries(PRO_LABELS).filter(([k]) => form.pro[k].username || form.pro[k].password);
  const filledPlatforms = Object.entries(PLATFORM_LABELS).filter(
    ([k]) => form.platforms[k].username || form.platforms[k].password
  );
  const filledBanking = BANKING_FIELDS.filter(([k]) => form.banking[k]);

  return (
    <div className="space-y-5">
      <Section title="Brand + Growth">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="review-brand-growth">
          {BRAND_GROWTH_FIELDS.map(([k, label]) => (
            <Item key={k} label={label} value={form.brandGrowth[k]} />
          ))}
        </div>
      </Section>

      <Section title="Personal">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Item label="Artist Name" value={form.artistName} />
          {[...PERSONAL_FIELDS, ...EMERGENCY_FIELDS].map(([k, label]) => (
            <Item key={k} label={label} value={form.personal[k]} />
          ))}
        </div>
      </Section>

      <Section title={`Team (${filledTeam.length})`}>
        {filledTeam.length === 0 ? (
          <p className="text-sm text-slate-500">No team contacts added.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filledTeam.map(([k, label]) => (
              <Item
                key={k}
                label={label}
                value={[form.team[k].name, form.team[k].email, form.team[k].phone].filter(Boolean).join(" · ")}
              />
            ))}
          </div>
        )}
      </Section>

      <Section title={`Socials (${filledSocials.length})`}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {filledSocials.map(([k, label]) => (
            <Item key={k} label={label} value={form.socials[k]} />
          ))}
          {filledSocials.length === 0 && <p className="text-sm text-slate-500">None added.</p>}
        </div>
      </Section>

      <Section title={`Sizes (${filledSizes.length})`}>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {filledSizes.map(([k, label]) => (
            <Item key={k} label={label} value={form.sizes[k]} />
          ))}
          {filledSizes.length === 0 && <p className="text-sm text-slate-500">None added.</p>}
        </div>
      </Section>

      <Section title="PRO Logins">
        <p className="text-sm text-slate-300" data-testid="review-pro-summary">
          {filledPros.length > 0
            ? `Credentials provided for: ${filledPros.map(([, l]) => l).join(", ")}`
            : "No PRO credentials provided."}
        </p>
      </Section>

      <Section title="Platform Access">
        <p className="text-sm text-slate-300" data-testid="review-platforms-summary">
          {filledPlatforms.length > 0
            ? `Credentials provided for: ${filledPlatforms.map(([, l]) => l).join(", ")}`
            : "No platform credentials provided."}
        </p>
      </Section>

      <Section title="Banking">
        {filledBanking.length === 0 ? (
          <p className="text-sm text-slate-500">No banking details provided.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {BANKING_FIELDS.map(([k, label]) => (
              <Item
                key={k}
                label={label}
                value={k === "accountNumber" && form.banking[k] ? `••••${form.banking[k].slice(-4)}` : form.banking[k]}
              />
            ))}
          </div>
        )}
      </Section>

      <Section title={`Documents (${form.documents.length})`}>
        {form.documents.length === 0 ? (
          <p className="text-sm text-slate-500">No documents uploaded.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {form.documents.map((d) => (
              <Item key={d.doc_type} label={DOC_TYPE_LABELS[d.doc_type] || d.doc_type} value={d.original_filename} />
            ))}
          </div>
        )}
      </Section>

      <div className="xy-card p-5 md:p-6 border-violet-500/30">
        <Field
          label="E-Signature (type your full legal name) *"
          testid="input-signature"
          placeholder="Your full legal name"
          value={form.signature}
          onChange={(e) => update("signature", e.target.value)}
        />
        <label className="mt-4 flex items-start gap-3 cursor-pointer" data-testid="label-agree">
          <input
            type="checkbox"
            data-testid="checkbox-agree"
            checked={form.agreed}
            onChange={(e) => update("agreed", e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-white/20 bg-white/5 accent-violet-500"
          />
          <span className="text-xs text-slate-300 leading-relaxed">
            I confirm the information provided is accurate and I authorize Xyion (a division of x | a —
            content + mgmt + tech) to use it for onboarding, royalty, and management purposes.
          </span>
        </label>
      </div>
    </div>
  );
};
